import React, { useState } from "react"
import {
	Grid,
	Tab,
	Tabs
} from "@material-ui/core"

import {
	Breadcrumb,
	Notification,
	Loading
} from "@/components"

import { integrationInfo } from "@/utils/integration"

import Settings from "@/pages/Admin/Plugins/GoogleDrivePlugin/Settings"

import useDidMount from "@/hooks/useDidMount"
import useStyles from "@/pages/Admin/Plugins/GoogleDrivePlugin/styles"

import ApiService from "@/services/Api"

import { GoogleDrivePluginSkeleton } from "@/skeletons/Admin/GoogleDrivePluginSkeleton"

type PluginTabType = "settings"

type PluginTab = {
	title: string
	type: PluginTabType
}

export type PluginSettings = {
	id: number
	instance_id: number
	status: "enabled" | "disabled"
	settings: {
		email: string
	}
}

const PLUGIN_TABS: PluginTab[] = [
	{
		title: "CONFIGURAÇÕES",
		type: "settings"
	}
]

const GoogleDrivePlugin = () => {
	const [currentPluginTabType, setCurrentPluginTabType] = useState<PluginTabType>(PLUGIN_TABS[0]?.type)
	const [plugin, setPlugin] = useState({} as PluginSettings)
	const [loading, setLoading] = useState<boolean>(true)

	const classes = useStyles()

	const handleLoadPlugin = async (): Promise<PluginSettings | null> => {
		try {
			const response = await ApiService.get<PluginSettings>("/plugin-settings/google-drive/consolidated")

			setPlugin(response.data)

			return response.data
		} catch (error) {
			Notification.error({ message: "Não foi possível carregar os dados do plugin." })
			return null
		} finally {
			setLoading(false)
		}
	}

	const handleOAuthPopupClosed = async () => {
		const loadedPlugin = await handleLoadPlugin()

		if (!loadedPlugin) {
			return
		}

		const pluginStatusToNoficationHandlerFn: Record<PluginSettings["status"], () => void> = {
			enabled: () => Notification.success({ message: "Plugin instalado com sucesso!" }),
			disabled: () => Notification.warning({ message: "Falha na instalação do plugin. Tente novamente garantindo que todas as permissões sejam fornecidas na janela de autenticação do Google." })
		}

		const notificationHandlerFn = pluginStatusToNoficationHandlerFn[loadedPlugin.status]

		if (notificationHandlerFn) {
			notificationHandlerFn()
		}
	}

	const handlePluginUninstalled = async () => {
		await handleLoadPlugin()

		Notification.success({ message: "Plugin desinstalado com sucesso!" })
	}

	const handleChangeCurrentPluginTabType = (type: PluginTabType): void => {
		setCurrentPluginTabType(type)
	}

	useDidMount(() => {
		handleLoadPlugin()
	})

	return (
		<Loading
			loading={loading}
			customLoadingElement={<GoogleDrivePluginSkeleton />}
		>
			<Grid
				container
				direction="column"
				spacing={3}
			>
				<Grid item>
					<Grid
						alignItems="center"
						justify="space-between"
						container
					>
						<Grid item>
							<Breadcrumb
								data={[
									{ name: "Plugins", pathname: "/admin/plugins" },
									{ name: "Google Drive", pathname: "/admin/plugins/google-drive" }
								]}
							/>
						</Grid>

						<Grid item>
							<img
								src={integrationInfo.GoogleDrive?.logo}
								alt="Google Drive logo"
								className={classes.logoImg}
							/>
						</Grid>
					</Grid>
				</Grid>

				<Grid item>
					<Tabs
						color="primary"
						value={currentPluginTabType}
						className={classes.tabs}
						onChange={(_, value) => handleChangeCurrentPluginTabType(value)}
						classes={{
							indicator: classes.tabIndicator
						}}
					>
						{PLUGIN_TABS.map(GoogleDrivePluginTab => (
							<Tab
								key={GoogleDrivePluginTab.title}
								label={GoogleDrivePluginTab.title}
								value={GoogleDrivePluginTab.type}
								className={classes.tab}
							/>
						))}
					</Tabs>
				</Grid>

				<Grid item>
					<Settings
						plugin={plugin}
						onOAuthPopupClosed={handleOAuthPopupClosed}
						onPluginUninstalled={handlePluginUninstalled}
					/>
				</Grid>
			</Grid>
		</Loading>
	)
}

export default GoogleDrivePlugin
